/**
 * BOOTSTRAP — D'vel CRM
 *
 * Boot sequence (ASYNC):
 * 1. await loadPlugins()       → glob discovery + registra plugin (+ entityTypes)
 * 2. await startMockWorker()   → avvia MSW (core handler + plugin handler via getMockHandlers)
 * 3. registerCoreEntityTypes() → registra Person, Business, Task, Message, Queue
 * 4. createAppStore()          → combina core + plugin reducers
 * 5. checkAuth()               → verifica JWT + workspace da localStorage
 * 6. render
 *
 * Nota: loadPlugins() precede startMockWorker() perche' i plugin possono
 * contribuire i propri handler tramite PluginManifest.getMockHandlers().
 * startMockWorker() e' async perche' MSW deve registrare il Service Worker.
 */

import './styles/main.scss';
import './i18n';
import { startMockWorker } from './api/mockWorker';
import { loadPlugins } from './plugin/pluginLoader';
import { registerCoreEntityTypes } from './entityRegistry/coreEntityRegistration';
import { createAppStore } from './store/store';
import { checkAuth } from './auth/authSlice';
import { startSessionResponder } from './auth/sessionHandshake';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeManager } from './ThemeManager';
import { applyBrandPalette, readCachedPalette, DEFAULT_PALETTE } from './brandPalette';
import { AppRouter } from './router/AppRouter';
import { ToastProvider } from './components/ToastProvider';

async function bootstrap() {
  // 1. Scopri e registra tutti i plugin (+ loro entityTypes)
  await loadPlugins();

  // 2. Mock Service Worker (no-op se VITE_USE_MOCKS non e' 'true').
  //    Dopo loadPlugins() cosi' da poter aggregare gli handler dei plugin.
  await startMockWorker();

  // 3. Registra i tipi entita' del core nell'EntityRegistry
  registerCoreEntityTypes();

  // 4. Crea lo store (core + plugin reducers)
  const store = createAppStore();

  // 5. Verifica autenticazione esistente. Prima avvio il responder dell'handshake
  //    cross-tab: se questa tab è/diventa autenticata, potrà cedere la sessione a
  //    nuove tab (es. link della mail) evitando loro il login. checkAuth, a sua
  //    volta, prova a farsi passare la sessione da altre tab se non ne ha una.
  startSessionResponder();
  store.dispatch(checkAuth());

  // 6. Render

  // 7. Anti-flash: applica subito le CSS var dall'ultima palette in cache
  //    (o il default) PRIMA del primo paint. Il fetch dal backend (in
  //    ThemeManager) la aggiornerà appena risponde.
  applyBrandPalette(readCachedPalette() ?? DEFAULT_PALETTE);

  // 8. Render
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <Provider store={store}>
        {/* ThemeManager dentro il Provider: deduce il brand dall'utente
            loggato e fornisce ThemeProvider + CssBaseline. */}
        <ThemeManager>
          <ToastProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </ToastProvider>
        </ThemeManager>
      </Provider>
    </StrictMode>
  );
}

bootstrap();
